import { useEffect, useMemo, useState } from "react";
import { UsersThree, Funnel, CheckCircle, TrendUp, CurrencyInr, Coins, FunnelSimple, X, GraduationCap } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const parseDate = (s) => new Date(String(s || "").replace("+0000", "+00:00"));
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const FSelect = ({ value, onChange, options, all, width = "w-44", testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} h-9 text-xs font-semibold bg-white`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72">
      {all && <SelectItem value="all">{all}</SelectItem>}
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Stat = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]"><Icon size={13} weight="duotone" className={accent ? "text-[#FF6B35]" : "text-[#0052FF]"} /> {label}</div>
    <div className="mt-1.5 font-heading text-xl font-extrabold tracking-tighter text-[#0A0D14]">{value}</div>
    {sub && <div className="text-[11px] text-[#8a90a0] mt-0.5">{sub}</div>}
  </div>
);

const ConvBadge = ({ pct, matched }) => {
  if (!matched) return <span className="text-[11px] text-[#c9cdd6] font-bold">—</span>;
  const tone = pct >= 30 ? "text-[#00953e] bg-success/10" : pct >= 15 ? "text-[#b07600] bg-warning/10" : "text-[#c73030] bg-danger/10";
  const bar = pct >= 30 ? "bg-[#00953e]" : pct >= 15 ? "bg-[#e3a008]" : "bg-[#c73030]";
  return (
    <div className="flex items-center gap-2 justify-end">
      <div className="w-16 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden hidden md:block">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${tone}`}>{pct}%</span>
    </div>
  );
};

const GROUPS = [{ v: "campaign", l: "By Campaign" }, { v: "form", l: "By Instant Form" }, { v: "ad", l: "By Ad" }];
const GROUP_LABEL = { campaign: "Campaign", form: "Instant Form", ad: "Ad" };

export default function ConversionReport() {
  const [rows, setRows] = useState(null);
  const [cpl, setCpl] = useState({});
  const [revenue, setRevenue] = useState(3000);
  const [formNames, setFormNames] = useState({});
  const [groupBy, setGroupBy] = useState("campaign");
  const [fSource, setFSource] = useState("all");
  const [fDays, setFDays] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "pos", dir: "desc" });

  useEffect(() => {
    api.get("/assessment/leads").then(({ data }) => {
      setRows(data.leads); setCpl(data.campaign_cpl || {}); setRevenue(data.revenue_per_admission || 3000);
    }).catch(() => setRows([]));
    api.get("/forms").then(({ data }) => setFormNames(Object.fromEntries((data.forms || []).map((f) => [f.id, f.name])))).catch(() => {});
  }, []);

  const formName = (id) => (id ? (formNames[id] || `Form …${String(id).slice(-4)}`) : "—");

  const dateRange = useMemo(() => {
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monday = new Date(sod.getTime() - ((sod.getDay() + 6) % 7) * 86400000);
    if (fDays === "all") return [null, null];
    if (fDays === "today") return [sod, null];
    if (fDays === "yesterday") return [new Date(sod.getTime() - 86400000), sod];
    if (fDays === "this_week") return [monday, null];
    if (fDays === "prev_week") return [new Date(monday.getTime() - 7 * 86400000), monday];
    if (fDays === "this_month") return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    if (fDays === "prev_month") return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    if (fDays === "custom") return [fFrom ? new Date(fFrom) : null, fTo ? new Date(new Date(fTo).getTime() + 86400000) : null];
    return [new Date(Date.now() - Number(fDays) * 86400000), null];
  }, [fDays, fFrom, fTo]);

  const filtered = useMemo(() => {
    const [start, end] = dateRange;
    return (rows || []).filter((l) => {
      const d = parseDate(l.created_at);
      return (fSource === "all" || l.outcome_source === fSource) &&
        (!start || d >= start) && (!end || d < end);
    });
  }, [rows, fSource, dateRange]);

  const report = useMemo(() => {
    const groups = {};
    for (const l of filtered) {
      const key = groupBy === "campaign" ? (l.campaign_name || "—")
        : groupBy === "form" ? formName(l.form_id)
        : (l.ad_name || "—");
      const g = groups[key] || (groups[key] = { name: key, leads: 0, matched: 0, pos: 0, neg: 0, pending: 0, spend: 0 });
      g.leads += 1;
      g.spend += cpl[l.campaign_name] || 0;
      if (l.matched) {
        g.matched += 1;
        if (l.positive === true) g.pos += 1;
        else if (l.positive === false) g.neg += 1;
        else g.pending += 1;
      }
    }
    let out = Object.values(groups).map((g) => ({
      ...g,
      conv: g.matched ? Math.round((g.pos / g.matched) * 100) : 0,
      cpl: g.leads ? Math.round(g.spend / g.leads) : 0,
      cpa: g.pos ? Math.round(g.spend / g.pos) : 0,
      revenue: g.pos * revenue,
      roas: g.spend ? Math.round(((g.pos * revenue) / g.spend) * 100) / 100 : 0,
    }));
    if (q) out = out.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()));
    const { key, dir } = sort;
    out.sort((a, b) => {
      const av = key === "name" ? a.name.toLowerCase() : (a[key] || 0);
      const bv = key === "name" ? b.name.toLowerCase() : (b[key] || 0);
      return (av < bv ? -1 : av > bv ? 1 : 0) * (dir === "asc" ? 1 : -1);
    });
    return out;
  }, [filtered, groupBy, cpl, revenue, q, sort, formNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const t = report.reduce((acc, g) => ({
      leads: acc.leads + g.leads, matched: acc.matched + g.matched, pos: acc.pos + g.pos, spend: acc.spend + g.spend,
    }), { leads: 0, matched: 0, pos: 0, spend: 0 });
    return { ...t,
      matchPct: t.leads ? Math.round((t.matched / t.leads) * 100) : 0,
      conv: t.matched ? Math.round((t.pos / t.matched) * 100) : 0,
      revenue: t.pos * revenue,
      roas: t.spend ? Math.round(((t.pos * revenue) / t.spend) * 100) / 100 : 0,
      cpa: t.pos ? Math.round(t.spend / t.pos) : 0 };
  }, [report, revenue]);

  const activeFilters = (fSource !== "all" ? 1 : 0) + (fDays !== "all" ? 1 : 0) + (q ? 1 : 0);
  const clearAll = () => { setFSource("all"); setFDays("all"); setFFrom(""); setFTo(""); setQ(""); };
  const daysLabel = { all: "all time", 7: "last 7 days", 14: "last 14 days", 30: "last 30 days",
                      today: "today", yesterday: "yesterday", this_week: "this week", prev_week: "previous week",
                      this_month: "this month", prev_month: "previous month", custom: "custom range" }[fDays] || "all time";

  const SortH = ({ k, label, right = true }) => (
    <th onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }))}
      data-testid={`report-sort-${k}`}
      className={`px-4 py-3 font-bold cursor-pointer select-none hover:text-[#0A0D14] whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
      {label}{sort.key === k ? (sort.dir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );

  return (
    <div className="space-y-5" data-testid="conversion-report-page">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="report-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A0D14] mr-1"><FunnelSimple size={15} weight="bold" /> Filters</span>
          <FSelect value={groupBy} onChange={setGroupBy} options={GROUPS} width="w-44" testId="report-group-by" />
          <FSelect value={fSource} onChange={setFSource} all="All outcome sources"
            options={[{ v: "zoho", l: "Zoho CRM" }, { v: "sheet", l: "Sheet upload" }, { v: "manual", l: "Manual mark" }]}
            width="w-44" testId="report-filter-source" />
          <FSelect value={fDays} onChange={setFDays} all="All time"
            options={[{ v: "today", l: "Today" }, { v: "yesterday", l: "Yesterday" },
                      { v: "this_week", l: "This week" }, { v: "prev_week", l: "Previous week" },
                      { v: "7", l: "Last 7 days" }, { v: "14", l: "Last 14 days" }, { v: "30", l: "Last 30 days" },
                      { v: "this_month", l: "This month" }, { v: "prev_month", l: "Previous month" }, { v: "custom", l: "Custom range…" }]}
            width="w-40" testId="report-filter-days" />
          {fDays === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="report-custom-date-range">
              <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="report-filter-date-from"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
              <span className="text-[11px] text-[#8a90a0] font-bold">to</span>
              <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="report-filter-date-to"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
            </div>
          )}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${GROUP_LABEL[groupBy].toLowerCase()}…`}
            data-testid="report-search-input" className="h-9 w-52 text-xs bg-white" />
          {activeFilters > 0 && (
            <button onClick={clearAll} className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[11px] font-bold text-[#525866] px-3 py-1.5 hover:bg-[#e7e9ee]" data-testid="report-filter-clear">
              <X size={11} weight="bold" /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-stat-cards">
        <Stat icon={UsersThree} label="Leads" value={totals.leads.toLocaleString("en-IN")} sub={daysLabel} testId="report-stat-leads" />
        <Stat icon={Funnel} label="Matched" value={`${totals.matched.toLocaleString("en-IN")} (${totals.matchPct}%)`} sub="have a sales outcome" testId="report-stat-matched" />
        <Stat icon={GraduationCap} label="Admissions" value={totals.pos.toLocaleString("en-IN")} sub="positive outcomes" accent testId="report-stat-admissions" />
        <Stat icon={TrendUp} label="Conversion" value={`${totals.conv}%`} sub="of matched leads" testId="report-stat-conversion" />
        <Stat icon={CurrencyInr} label="Revenue" value={inr(totals.revenue)} sub={`admissions × ${inr(revenue)}`} accent testId="report-stat-revenue" />
        <Stat icon={CurrencyInr} label="Est. spend" value={inr(totals.spend)} sub="leads × campaign CPL" testId="report-stat-spend" />
        <Stat icon={TrendUp} label="ROAS" value={totals.spend ? `${totals.roas}x` : "—"} sub="revenue ÷ est. spend" accent testId="report-stat-roas" />
        <Stat icon={Coins} label="Cost / admission" value={totals.pos ? inr(totals.cpa) : "—"} sub="the metric that matters" testId="report-stat-cpa" />
      </div>

      <div>
        <h2 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">
          Admissions report card <span className="text-xs font-normal text-[#8a90a0] ml-1">{report.length} {GROUP_LABEL[groupBy].toLowerCase()}s · click any column to sort</span>
        </h2>
        {rows === null ? (
          <Skeleton className="h-64 rounded-xl mt-3" />
        ) : (
          <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[1180px]" data-testid="report-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-[11px] uppercase tracking-wide text-[#8a90a0]">
                  <SortH k="name" label={GROUP_LABEL[groupBy]} right={false} />
                  <SortH k="leads" label="Leads" />
                  <SortH k="matched" label="Matched" />
                  <SortH k="pos" label="Admissions" />
                  <SortH k="neg" label="Negative" />
                  <SortH k="pending" label="Follow-up" />
                  <SortH k="conv" label="Conversion" />
                  <SortH k="cpl" label="CPL" />
                  <SortH k="spend" label="Est. spend" />
                  <SortH k="revenue" label="Revenue" />
                  <SortH k="roas" label="ROAS" />
                  <SortH k="cpa" label="Cost / admission" />
                </tr>
              </thead>
              <tbody>
                {report.length === 0 ? (
                  <tr><td colSpan={12} className="px-4 py-8 text-center text-xs text-[#8a90a0]" data-testid="report-empty">No data for these filters — widen the date range or clear filters.</td></tr>
                ) : report.map((g) => (
                  <tr key={g.name} className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFB]" data-testid={`report-row-${g.name}`}>
                    <td className="px-4 py-3"><div className="max-w-[260px] font-bold text-[#0A0D14] text-xs truncate" title={g.name}>{g.name}</div></td>
                    <td className="px-4 py-3 text-right text-xs text-[#525866]">{g.leads.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right text-xs text-[#525866]">{g.matched.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right"><span className="inline-flex items-center gap-1 text-xs font-extrabold text-[#00953e]"><CheckCircle size={13} weight="fill" /> {g.pos}</span></td>
                    <td className="px-4 py-3 text-right text-xs text-[#c73030] font-semibold">{g.neg}</td>
                    <td className="px-4 py-3 text-right text-xs text-[#b07600] font-semibold">{g.pending}</td>
                    <td className="px-4 py-3"><ConvBadge pct={g.conv} matched={g.matched} /></td>
                    <td className="px-4 py-3 text-right text-xs text-[#525866]">{g.cpl ? inr(g.cpl) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs text-[#525866]">{inr(g.spend)}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold text-[#00953e]">{g.pos ? inr(g.revenue) : "—"}</td>
                    <td className="px-4 py-3 text-right text-xs font-bold">
                      <span className={g.roas >= 1 ? "text-[#00953e]" : g.roas > 0 ? "text-[#c73030]" : "text-[#c9cdd6]"}>{g.spend && g.pos ? `${g.roas}x` : "—"}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs font-extrabold text-[#0A0D14]">{g.pos ? inr(g.cpa) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-[#8a90a0] mt-2">
          Est. spend = leads in view × that campaign's CPL. Revenue = admissions × ₹{revenue.toLocaleString("en-IN")} (set in Meta CAPI settings on Lead Assessment). ROAS = revenue ÷ est. spend. Conversion is colored: <b className="text-[#00953e]">≥30%</b> · <b className="text-[#b07600]">15–29%</b> · <b className="text-[#c73030]">&lt;15%</b>. Outcomes come from Zoho CRM / sheet uploads / manual marks.
        </p>
      </div>
    </div>
  );
}
